import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { CheckCircleIcon, CalendarClockIcon, Building2 } from 'lucide-react'
import { DataListToolbar } from '@/components/data/DataListToolbar'
import {
  PersistentListCard,
  type PersistentListCardEmptyProps,
} from '@/components/data/PersistentListCard'
import {
  getPaymentCyclesCompletedApi,
  type PaymentCycleCompleted,
  type PaymentCyclesCompletedResponse,
} from '@/lib/api'

const PAGE_SIZE = 20

function formatAmount(amount: string, currencySymbol?: string | null): string {
  const n = Number(amount)
  const symbol = currencySymbol || '$'
  if (!Number.isFinite(n)) return `${symbol}${amount}`
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

export const Route = createFileRoute('/dashboard/completed-payments')({
  component: () => {
    const [response, setResponse] = useState<PaymentCyclesCompletedResponse | null>(null)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)

    const fetchData = useCallback(
      async (
        targetPage: number,
        isRefresh: boolean,
        signal?: { cancelled: boolean },
      ) => {
        if (signal?.cancelled) return
        if (isRefresh) setRefreshing(true)
        else setLoading(true)
        setFetchError(null)
        try {
          const data = await getPaymentCyclesCompletedApi(targetPage, PAGE_SIZE)
          if (signal?.cancelled) return
          setResponse(data)
        } catch (err) {
          if (signal?.cancelled) return
          setFetchError(
            err instanceof Error ? err.message : 'Failed to load completed payments',
          )
        } finally {
          if (!signal?.cancelled) {
            setLoading(false)
            setRefreshing(false)
          }
        }
      },
      [],
    )

    useEffect(() => {
      const signal = { cancelled: false }
      fetchData(page, false, signal)
      return () => {
        signal.cancelled = true
      }
    }, [page, fetchData])

    useEffect(() => {
      if (!response) return
      const newTotalPages = Math.max(1, Math.ceil(response.total / PAGE_SIZE))
      if (page > newTotalPages) setPage(1)
    }, [response, page])

    const cycles: PaymentCycleCompleted[] = response?.data ?? []
    const total = response?.total ?? 0
    const initialLoaded = response !== null || fetchError !== null
    const isInitialLoad = loading && !initialLoaded

    const handleRefresh = () => fetchData(page, true)

    const subtitle = isInitialLoad ? (
      <span className="inline-block h-4 w-56 animate-pulse rounded bg-muted align-middle" />
    ) : total === 0 && !fetchError ? (
      <>No completed payment cycles yet.</>
    ) : (
      <>
        <span className="font-medium text-foreground">{total}</span>{' '}
        {total === 1 ? 'cycle' : 'cycles'} completed
      </>
    )

    const empty: PersistentListCardEmptyProps | null = {
      icon: <CheckCircleIcon className="size-5" />,
      title: 'No completed payment cycles.',
      description: 'Completed payment cycles will appear here once you confirm payouts.',
    }

    return (
      <div className="flex flex-col gap-6">
        <DataListToolbar
          title="Completed payments"
          subtitle={subtitle}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          loading={loading}
        />

        <PersistentListCard<PaymentCycleCompleted>
          loading={loading}
          initialLoaded={initialLoaded}
          error={fetchError}
          errorTitle="Couldn't load completed cycles"
          onRetry={handleRefresh}
          empty={empty}
          items={cycles}
          itemKey={(c) => c.payment_cycle_id}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          pageLoading={loading}
          renderItem={(cycle) => (
            <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 transition-all hover:bg-muted/30 hover:shadow-md sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10 text-[var(--brand-mint)]">
                  <CheckCircleIcon className="size-5" />
                </div>
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {cycle.title ?? `${cycle.creator_name}'s Circle`} · Cycle #{cycle.cycle_number}
                    </span>
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      Paid
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarClockIcon className="size-3.5" />
                      Due {formatDate(cycle.due_date)}
                    </span>
                    <span>Spots: {cycle.spots_progress}</span>
                    {cycle.paid_at && (
                      <span className="inline-flex items-center gap-1.5">
                        <CheckCircleIcon className="size-3.5" />
                        Paid {formatDate(cycle.paid_at)}
                      </span>
                    )}
                    {cycle.paying_org_name && (
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="size-3.5" />
                        Paid by {cycle.paying_org_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 sm:justify-end">
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm font-semibold tabular-nums">
                    {formatAmount(cycle.cycle_amount_per_person, cycle.currency_symbol)}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatAmount(cycle.total_expected, cycle.currency_symbol)}
                  </span>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    )
  },
})
